import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import qs from 'qs';
import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import { useMediaQuery } from '@mui/material';

import usePageMeta from 'hooks/use-page-meta';
import usePersistent from 'hooks/use-persistent';
import useSendGaPageView from 'hooks/use-send-ga-page-view';
import useStores from 'hooks/use-stores';
import SearchTemplates from 'main/components/search/SearchTemplates';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'modules/elastic';
import isomorphicLocalStorage from 'modules/isomorphic-local-storage';
import BulletinTypes from 'pages/BulletinTypes';
import PromoBlock from 'pages/components/PromoBlock';
import Landing from 'pages/Landing';
import Layout from 'pages/Search/components/Layout';
import { qsReplacer } from 'utils/qs-replacer';
import { safeDecodeUriComponent } from 'utils/safe-decode-uri-component';
import Utils from 'utils/Utils';

import ElasticFilter from './components/ElasticFilter';
import Filter from './components/Filter';
import SearchResults from './components/SearchResults';
import Widgets from './components/Widgets';
import useStyles from './styles';

const Database = () => {
    const { classes } = useStyles();
    const history = useHistory();

    const sendGaPageView = useSendGaPageView();
    const paramsRef = usePersistent(useParams<{ tag?: string }>());

    const { naslStore, statsStore, searchStore, antiBotStore, sTemplatesStore } = useStores();
    const search = searchStore.query || searchStore.software;
    usePageMeta({
        title: search
            ? 'Vulners Database - ' + Utils.trunc(100, search)
            : 'CVE Search Engine - Security Vulnerabilities and Exploits Search Tool',
        description: 'Vulners - Vulnerability DataBase'
    });

    const layoutRef = useRef<{ scrollTop: () => void }>(null);

    const onPageChange = useCallback(
        ({ query, software, version, state, skip, size }) => {
            if (history.location.pathname.startsWith('/search/tags')) {
                history.push({
                    pathname: history.location.pathname,
                    search: qs.stringify({ skip, size }, { filter: qsReplacer })
                });
            } else {
                history.push({
                    pathname: '/search',
                    search: qs.stringify(
                        {
                            query,
                            software,
                            version,
                            state: ElasticQueryBuilder.stateToString(state),
                            skip,
                            size
                        },
                        { filter: qsReplacer }
                    )
                });
                searchStore.setParameters({
                    query: searchStore.type === SearchType.elastic ? state.searchTerm ?? '' : query ?? '',
                    software,
                    version,
                    state,
                    type: searchStore.type
                });
            }
            layoutRef.current?.scrollTop();
            searchStore.setPageSize(size);
            searchStore.performFetch({ skip });
            antiBotStore.randomCollectData();
            sendGaPageView({ title: 'Search' });
        },
        [antiBotStore, history, searchStore, sendGaPageView]
    );

    const refetch = useCallback(() => {
        const { search, pathname } = window.location;
        const url = new URLSearchParams(search);
        const query = url.get('query')?.trim() || '';
        const software = url.get('software')?.trim() || '';
        const version = url.get('version')?.trim() || '';
        const skip = url.get('skip')?.trim();
        const size = url.get('size')?.trim();
        const state = url.get('state')?.trim() || '';

        let type = searchStore.type;
        let queryParameter = safeDecodeUriComponent(query);
        let stateParameter = {};

        switch (true) {
            case pathname.startsWith('/search/tags'): {
                type = SearchType.simple;
                stateParameter = {};
                const template = sTemplatesStore.templates.find(({ aliasUrl }) => pathname.includes(aliasUrl));
                queryParameter = template
                    ? template.query
                    : paramsRef.current.tag
                    ? `enchantments.tags:"${safeDecodeUriComponent(paramsRef.current.tag)}"`
                    : '';
                break;
            }
            case !!state: {
                type = SearchType.elastic;
                const parsed = ElasticQueryBuilder.stringToState(state);
                stateParameter = parsed;
                queryParameter = parsed.searchTerm ?? '';
                break;
            }
            case !!query && !software:
                type = SearchType.simple;
                break;
            case !query && !!software:
                type = SearchType.product;
                break;
            default:
                break;
        }

        searchStore.setParameters({
            query: queryParameter,
            software,
            version,
            type,
            state: stateParameter
        });

        if (size) {
            searchStore.pageSize = Number(size);
        }

        searchStore.performFetch({ skip });
    }, [paramsRef, sTemplatesStore, searchStore]);

    useEffect(() => {
        const unsubscribe = history.listen((_, action) => {
            if (action === 'POP') {
                refetch();
            }
        });
        return () => {
            unsubscribe();
            searchStore.resetParameters();
        };
    }, [history, refetch, searchStore]);

    useEffect(() => {
        naslStore.loadTypes();
        statsStore.loadStats();
        statsStore.loadBulletinStats();
        sTemplatesStore.loadData();

        refetch();

        if (isomorphicLocalStorage.getItem(Landing.LS_LANDING_VISITED) === 'true') {
            isomorphicLocalStorage.setItem(Landing.LS_LANDING_SHOW, 'false');
        }

        sendGaPageView({ title: 'Search' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shouldShowSearch = !isEmpty(searchStore.query) || !isEmpty(searchStore.software) || searchStore.hasTerms;

    const shouldShowFilter =
        useMediaQuery('(min-width:1024px)') &&
        shouldShowSearch &&
        [SearchType.simple, SearchType.elastic].includes(searchStore.type);

    return (
        <Layout
            ref={layoutRef}
            main={
                <>
                    <SearchTemplates />
                    {shouldShowSearch ? (
                        <SearchResults onPageChange={onPageChange} />
                    ) : (
                        <div className={classes.wrapper}>
                            <Widgets />
                            <BulletinTypes
                                breakpoint={960}
                                classes={{ title: classes.bulletinTypesTitle, button: classes.bulletinTypesButton }}
                            />
                            <PromoBlock />
                        </div>
                    )}
                </>
            }
            additional={
                shouldShowFilter ? searchStore.type === SearchType.elastic ? <ElasticFilter /> : <Filter /> : null
            }
        />
    );
};

export default observer(Database);
